import { type JSX } from "react";
import { Button } from "../shared/ui/Button/Button";

type PaginationDevicesMessagesProps = {
    limit: number;
    total: number;
    page: number;
    setPage: (page: number) => void;
};

export const PaginationDevicesMessages = ({
    limit,
    total,
    page,
    setPage,
}: PaginationDevicesMessagesProps): JSX.Element => {
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const handlePageChange = (newPage: number): void => {
        const clamped = Math.min(totalPages, Math.max(1, newPage));
        setPage(clamped);
    };

    const getPageItems = (curr: number, totalPgs: number): Array<number | "..."> => {
        const maxNumbers = 5;

        if (totalPgs <= maxNumbers) {
            return Array.from({ length: totalPgs }, (_, i) => i + 1);
        }

        const leftEllipsis = curr > 3;
        const rightEllipsis = curr < totalPgs - 2;

        if (!leftEllipsis && rightEllipsis) {
            return [1, 2, 3, 4, "...", totalPgs];
        }

        if (leftEllipsis && !rightEllipsis) {
            return [1, "...", totalPgs - 3, totalPgs - 2, totalPgs - 1, totalPgs];
        }

        return [1, "...", curr - 1, curr, curr + 1, "...", totalPgs];
    };

    const pageItems = Array.from(new Set(getPageItems(page, totalPages)));

    return (
        <div className="pt-3 px-3 pb-4 rounded-b-2xl flex items-center justify-between ">
            <div className="gap-2 flex items-center">
                <Button
                    variant="outline"
                    icon="chevron_left"
                    iconSize={16}
                    size="xs"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                />

                {pageItems.map((item, idx) =>
                    item === "..." ? (
                        <Button
                            key={`ellipsis-${idx}`}
                            title="…"
                            variant="outline"
                            size="xs"
                            disabled
                            className="bg-white"
                        />
                    ) : (
                        <Button
                            key={`page-${item}`}
                            title={item.toString()}
                            variant={item === page ? "primary" : "outline"}
                            size="xs"
                            onClick={() => handlePageChange(item as number)}
                        />
                    )
                )}

                <Button
                    variant="outline"
                    icon="chevron_right"
                    iconSize={16}
                    size="xs"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                />
            </div>
        </div>
    );
};